import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { useContent } from "@/lib/content";
import { useActivePromo } from "@/lib/promo";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronLeft, ChevronRight, Tag } from "lucide-react";

const ROTATE_MS = 4000;
const MAX_SLIDES = 10;

/**
 * Only products a visitor could buy this second: on the Buy Now catalogue, with
 * stock on hand, a real price and a photo to show. The backend serves the shelf
 * the admin has starred (newest available stock until they pick any), and this
 * filter is a final guard — the shop page itself is untouched.
 */
const available = (p) => p.stock > 0 && Number(p.price) > 0 && !!p.image && !!p.name;

/**
 * Auto-rotating shelf of real catalogue products. Three are visible on a laptop,
 * one on a phone; the track slides by one product every few seconds and the
 * arrows hand control back to the visitor. Every card opens that product's page.
 */
export const FeaturedProducts = () => {
  const { c } = useContent();
  const promo = useActivePromo();
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [perView, setPerView] = useState(3);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const fit = () => setPerView(window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 2 : 3);
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  useEffect(() => {
    api.get("/products/featured")
      .then(({ data }) => setItems((data.products || []).filter(available).slice(0, MAX_SLIDES)))
      .catch(() => {});
  }, []);

  // With fewer products than slots, the cards widen to fill the row instead of
  // leaving a gap where a slide would have been.
  const per = Math.max(1, Math.min(perView, items.length));
  const stops = Math.max(1, items.length - per + 1);

  const step = useCallback((dir) => {
    setIndex((i) => (i + dir + stops) % stops);
  }, [stops]);

  useEffect(() => { setIndex((i) => Math.min(i, stops - 1)); }, [stops]);

  useEffect(() => {
    if (paused || items.length < 2) return undefined;
    const t = setInterval(() => step(1), ROTATE_MS);
    return () => clearInterval(t);
  }, [paused, items.length, step]);

  if (items.length === 0) return null;

  return (
    <section className="bg-card/40" data-testid="home-featured">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-14 lg:pt-16 pb-24 lg:pb-28">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="overline mb-3">{c("home.featured.overline")}</div>
            <h2 className="font-display text-4xl lg:text-5xl max-w-xl" data-testid="home-featured-title">
              {c("home.featured.title")}
            </h2>
            <p className="text-muted-foreground mt-4 max-w-md">{c("home.featured.subtitle")}</p>
            {promo && (
              <div
                className="inline-flex items-center gap-2 mt-5 pl-3 pr-4 py-1.5 rounded-full bg-secondary/15 border border-secondary/40 text-secondary"
                data-testid="featured-promo-note"
              >
                <Tag size={13} />
                <span className="text-xs tracking-wide">
                  {promo.benefit} · code <span className="font-semibold tracking-[0.15em]">{promo.code}</span>
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => step(-1)}
              aria-label="Previous product"
              data-testid="featured-prev"
              className="w-11 h-11 rounded-full border border-border bg-background hover:border-primary/50 hover:text-primary flex items-center justify-center transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => step(1)}
              aria-label="Next product"
              data-testid="featured-next"
              className="w-11 h-11 rounded-full border border-border bg-background hover:border-primary/50 hover:text-primary flex items-center justify-center transition-colors"
            >
              <ChevronRight size={18} />
            </button>
            <Link to="/shop">
              <Button className="btn-action h-11 px-6" data-testid="featured-shop-btn">
                {c("home.featured.cta")} <ArrowRight size={15} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        <div
          className="overflow-hidden"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="featured-viewport"
        >
          <div
            className="flex transition-transform duration-700 ease-out"
            style={{
              width: `${(items.length / per) * 100}%`,
              transform: `translateX(-${index * (100 / items.length)}%)`,
            }}
            data-testid="featured-track"
          >
            {items.map((p) => (
              <Link
                key={p.id}
                to={`/shop/${p.id}`}
                className="group px-3 shrink-0"
                style={{ width: `${100 / items.length}%` }}
                data-testid={`featured-item-${p.id}`}
              >
                <div className="rounded-[1.75rem] border border-border bg-background overflow-hidden hover:border-primary/40 transition-colors h-full">
                  <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                    <img
                      src={mediaUrl(p.image)}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                    />
                    {promo && (
                      <div
                        className="absolute top-4 left-4 promo-ribbon"
                        data-testid={`featured-promo-badge-${p.id}`}
                        data-promo-code={promo.code}
                      >
                        <span className="block text-[9px] uppercase tracking-[0.22em] opacity-80 leading-none">
                          {promo.discount_type === "flat" ? `₹${promo.value} off` : `${promo.value}% off`}
                        </span>
                        <span className="block text-[12px] font-semibold tracking-[0.16em] leading-tight mt-0.5">
                          {promo.code}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-2xl leading-tight mb-2" data-testid={`featured-name-${p.id}`}>{p.name}</h3>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-medium" data-testid={`featured-price-${p.id}`}>
                        ₹{Number(p.price).toLocaleString("en-IN")}
                      </span>
                      <span className="text-sm text-primary inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        View <ArrowRight size={13} />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="flex justify-center gap-2 mt-10" data-testid="featured-dots">
          {Array.from({ length: stops }).map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`featured-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === index ? "w-8 bg-primary" : "w-2.5 bg-border hover:bg-primary/40"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
